import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Stack,
  VStack,
  HStack,
  Heading,
  Text,
  Link,
  useToast,
  Icon,
  Flex,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Collapse,
  useColorModeValue,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, LockIcon } from "@chakra-ui/icons";
import { FaWindows } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { buildTenantRedirectUrl } from "../utils/tenant";
import { useNavigate } from "react-router-dom";
import { FORGOT_PASSWORD_URL } from "../services/api";
import LoginLeftPanelGradient from "../components/LoginLeftPanelGradient";
import { getMicrosoftAccessToken } from "../services/microsoftAuth";

const LAST_USED_ACCOUNTS_KEY = "last_used_accounts";
const MAX_STORED_ACCOUNTS = 3;

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "";
  const [name, domain] = email.split("@");
  if (name.length <= 2) return `${name}*****@${domain}`;
  return `${name.slice(0, 2)}*****@${domain}`;
};

const getDisplayName = (email) => {
  if (!email) return "";
  const username = email.split("@")[0] || "";
  return username.charAt(0).toUpperCase() + username.slice(1);
};

const readStoredAccounts = () => {
  try {
    const storedAccounts = JSON.parse(
      localStorage.getItem(LAST_USED_ACCOUNTS_KEY) || "[]",
    );

    if (Array.isArray(storedAccounts) && storedAccounts.length > 0) {
      return storedAccounts
        .filter((user) => user?.email)
        .sort((a, b) => b.lastLogin - a.lastLogin)
        .slice(0, MAX_STORED_ACCOUNTS);
    }

    const legacyEmail = localStorage.getItem("lastUserEmail");
    if (!legacyEmail) return [];

    return [
      {
        id: legacyEmail,
        email: legacyEmail,
        maskedEmail: maskEmail(legacyEmail),
        lastLogin: Date.now(),
      },
    ];
  } catch {
    return [];
  }
};

const persistStoredAccounts = (accounts) => {
  const normalizedAccounts = accounts
    .filter((user) => user?.email)
    .sort((a, b) => b.lastLogin - a.lastLogin)
    .slice(0, MAX_STORED_ACCOUNTS);

  localStorage.setItem(
    LAST_USED_ACCOUNTS_KEY,
    JSON.stringify(normalizedAccounts),
  );

  if (normalizedAccounts[0]?.email) {
    localStorage.setItem("lastUserEmail", normalizedAccounts[0].email);
  } else {
    localStorage.removeItem("lastUserEmail");
  }

  return normalizedAccounts;
};

const upsertStoredAccount = (accounts, email) => {
  const nextAccount = {
    id: email,
    email,
    maskedEmail: maskEmail(email),
    lastLogin: Date.now(),
  };

  return persistStoredAccounts([
    nextAccount,
    ...accounts.filter((user) => user.email !== email),
  ]);
};

const LoginPage = ({ onSwitch }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    companyName: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [storedAccounts, setStoredAccounts] = useState(readStoredAccounts);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [useAnotherAccount, setUseAnotherAccount] = useState(false);
  const [showStoredEmail, setShowStoredEmail] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const passwordInputRef = useRef(null);
  const {
    isOpen: isForgotPasswordOpen,
    onOpen: onForgotPasswordOpen,
    onClose: onForgotPasswordClose,
  } = useDisclosure();
  const toast = useToast();
  const { login, register, microsoftAuth } = useAuth();
  const navigate = useNavigate();

  const handleLoginChange = (e) =>
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });

  const handleRegisterChange = (e) =>
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const handleLoginKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    } else if (e.key === "Escape" && selectedAccount) {
      handleUseDifferentAccount();
    }
  };

  const handleRegisterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRegister();
    }
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setUseAnotherAccount(false);
    setShowStoredEmail(false);
    setLoginForm({ email: account.email, password: "" });
    setActiveTab(0);
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 0);
  };

  const handleUseDifferentAccount = () => {
    setLoginForm({ email: "", password: "" });
    setSelectedAccount(null);
    setUseAnotherAccount(true);
    setShowStoredEmail(false);
  };

  const handleClearStoredAccount = (email) => {
    const nextAccounts = persistStoredAccounts(
      storedAccounts.filter((account) => account.email !== email),
    );
    setStoredAccounts(nextAccounts);
    if (selectedAccount?.email === email) {
      setSelectedAccount(null);
      setLoginForm({ email: "", password: "" });
    }
    if (nextAccounts.length === 0) {
      setUseAnotherAccount(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      return toast({
        title: "Email Required",
        description: "Please enter your email address.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }

    setIsSendingReset(true);
    try {
      const response = await fetch(FORGOT_PASSWORD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          frontendUrl: window.location.origin, // Send current frontend URL
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Reset Link Sent",
          description: "Check your email for password reset instructions.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setForgotPasswordEmail("");
        onForgotPasswordClose();
      } else {
        throw new Error(data.message || "Failed to send reset link");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message || "Failed to send reset link. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      const { email, password, companySlug } = loginForm;

      // Replace "mis" with "iom" before sending the request
      const adjustedCompanySlug = companySlug === "mis" ? "iom" : companySlug;

      const response = await login({
        email,
        password,
        companySlug: adjustedCompanySlug,
      });

      // Handle multiple companies scenario
      if (response.requiresCompanySelection) {
        setAvailableCompanies(response.companies || []);
        setShowCompanySelector(true);
        toast({
          title: "Multiple companies found",
          description: "Please select your company to continue",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (response.success) {
        // Store account for quick login next time
        const nextAccounts = persistStoredAccounts([
          { email, name: response.user?.name },
          ...storedAccounts.filter((acc) => acc.email !== email),
        ]);
        setStoredAccounts(nextAccounts);

        toast({
          title: "Login successful!",
          status: "success",
          duration: 4000,
          isClosable: true,
        });

        const tenantSlug = response.company?.slug;
        const tenantRedirectUrl = buildTenantRedirectUrl(tenantSlug);
        if (tenantRedirectUrl) {
          window.location.assign(tenantRedirectUrl);
          return;
        }

        navigate("/dashboard");
      } else {
        toast({
          title: "Login failed!",
          description: response.message || "Invalid credentials",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Login failed!",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = async (companySlug) => {
    try {
      setIsLoading(true);
      setShowCompanySelector(false);

      const { email, password } = loginForm;
      const response = await login({
        email,
        password,
        companySlug,
      });

      if (response.success) {
        const nextAccounts = persistStoredAccounts([
          { email, name: response.user?.name },
          ...storedAccounts.filter((acc) => acc.email !== email),
        ]);
        setStoredAccounts(nextAccounts);

        toast({
          title: "Login successful!",
          status: "success",
          duration: 4000,
          isClosable: true,
        });

        const tenantRedirectUrl = buildTenantRedirectUrl(
          response.company?.slug,
        );
        if (tenantRedirectUrl) {
          window.location.assign(tenantRedirectUrl);
          return;
        }

        navigate("/dashboard");
      } else {
        toast({
          title: "Login failed!",
          description: response.message || "Invalid credentials",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Login failed!",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (
      !registerForm.name ||
      !registerForm.email ||
      !registerForm.password ||
      !registerForm.companyName
    ) {
      return toast({
        title: "Missing Fields",
        description: "Name, Email, Password, and Company Name are required.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      return toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (registerForm.password.length < 6) {
      return toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters long.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }

    setIsLoading(true);
    const result = await register({
      name: registerForm.name,
      email: registerForm.email,
      password: registerForm.password,
      phone: registerForm.phone,
      companyName: registerForm.companyName,
    });
    setIsLoading(false);

    if (result.success) {
      toast({
        title: "Registration Successful",
        description: "Welcome to NoraHuman!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      const tenantSlug = result.company?.slug;
      const tenantRedirectUrl = buildTenantRedirectUrl(tenantSlug);
      if (tenantRedirectUrl) {
        window.location.assign(tenantRedirectUrl);
        return;
      }

      navigate("/");
    } else {
      toast({
        title: "Registration Failed",
        description: result.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleMicrosoftAuth = async (mode) => {
    if (mode === "register" && !registerForm.companyName?.trim()) {
      return toast({
        title: "Company Name Required",
        description: "Enter your company name before signing up with Outlook.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }

    setIsMicrosoftLoading(true);

    try {
      const accessToken = await getMicrosoftAccessToken();
      const result = await microsoftAuth({
        accessToken,
        mode,
        companyName: registerForm.companyName,
      });

      if (result.success) {
        const accountEmail = result.user?.email;
        if (accountEmail) {
          const nextAccounts = upsertStoredAccount(
            storedAccounts,
            accountEmail,
          );
          setStoredAccounts(nextAccounts);
          setSelectedAccount(nextAccounts[0] || null);
          setUseAnotherAccount(false);
        }

        toast({
          title:
            mode === "register"
              ? "Outlook Sign Up Successful"
              : "Outlook Sign In Successful",
          description:
            mode === "register"
              ? "Your account has been created."
              : "Welcome back!",
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        const tenantSlug = result.company?.slug;
        const tenantRedirectUrl = buildTenantRedirectUrl(tenantSlug);
        if (tenantRedirectUrl) {
          window.location.assign(tenantRedirectUrl);
          return;
        }

        navigate("/");
        return;
      }

      toast({
        title: "Outlook Authentication Failed",
        description: result.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Outlook Authentication Failed",
        description:
          error.message || "Unable to authenticate with Outlook right now.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const hasStoredAccounts = storedAccounts.length > 0;
  const primaryAccount = storedAccounts[0] || null;
  const secondaryAccounts = storedAccounts.slice(1, MAX_STORED_ACCOUNTS);
  const showAccountSelector =
    activeTab === 0 &&
    hasStoredAccounts &&
    !useAnotherAccount &&
    !selectedAccount;
  const selectedDisplayAccount = selectedAccount || primaryAccount;

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const headerTextColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const textColorDark = useColorModeValue("gray.800", "gray.100");
  const labelColor = useColorModeValue("gray.700", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const inputBgHover = useColorModeValue("white", "gray.600");
  const inputBorderColor = useColorModeValue("gray.200", "gray.600");
  const inputBorderHover = useColorModeValue("gray.400", "gray.500");
  const tabListBg = useColorModeValue("gray.100", "gray.700");
  const tabHoverBg = useColorModeValue("gray.200", "gray.600");
  const dividerColor = useColorModeValue("gray.400", "gray.600");
  const accountBoxBg = useColorModeValue(
    "linear-gradient(180deg, rgba(247,250,255,1) 0%, rgba(255,255,255,1) 100%)",
    "linear-gradient(180deg, rgba(45,55,72,1) 0%, rgba(26,32,44,1) 100%)",
  );
  const accountBoxBorder = useColorModeValue("blue.100", "blue.800");
  const secondaryAccountBg = useColorModeValue("white", "gray.700");
  const secondaryAccountBorder = useColorModeValue("gray.200", "gray.600");
  const tooltipBg = useColorModeValue("gray.700", "gray.400");
  const tooltipColor = useColorModeValue("white", "gray.800");
  const iconColor = useColorModeValue("gray.500", "gray.400");
  const outlineButtonColor = useColorModeValue("gray.700", "gray.400");
  const outlineButtonBorder = useColorModeValue("gray.400", "gray.600");
  const outlineButtonHoverBg = useColorModeValue("gray.50", "gray.700");
  const outlineButtonActiveBg = useColorModeValue("gray.100", "gray.600");
  const modalBg = useColorModeValue("white", "gray.800");
  const inputDisabledBg = useColorModeValue("gray.100", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.500", "gray.500");

  // Determine which left panel to show based on odd/even date
  return (
    <Flex
      width="100vw"
      minH="100dvh"
      height={{ base: "auto", md: "100dvh" }}
      bg="gray.50"
      align="stretch"
      justify="stretch"
      overflow={{ base: "auto", md: "hidden" }}
    >
      <Flex
        w="full"
        maxW="none"
        h={{ base: "auto", md: "full" }}
        minH="100dvh"
        bg="white"
        borderRadius="0"
        overflow={{ base: "visible", md: "hidden" }}
        boxShadow="none"
        flexDir={{ base: "column", md: "row" }}
      >
        <LoginLeftPanelGradient />
        {/* {isOddDay ? <LoginLeftPanelWaves /> : <LoginLeftPanelWaves />} */}

        {/* Right Panel - Login/Register Form */}
        <Flex
          width={{ base: "100%", md: "42%", lg: "38%", xl: "34%" }}
          h={{ base: "auto", md: "full" }}
          minH={{ base: "auto", md: "100dvh" }}
          flexShrink={0}
          bg={bgColor}
          direction="column"
        >
          {/* Header */}
          <HStack justify="flex-end" px={{ base: 5, sm: 6 }} py={{ base: 4, md: 6 }} spacing={4}>
            <Text fontSize="sm" color={headerTextColor}>
              Need help?
            </Text>
            <Link
              fontSize="sm"
              color="#0B4885"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              Contact Support
            </Link>
          </HStack>

          {/* Form Container */}
          <Flex
            flex="1"
            minH={0}
            direction="column"
            px={{ base: 5, sm: 6, md: 8, lg: 10 }}
            py={{ base: 5, md: 8 }}
            overflowY={{ base: "visible", md: "auto" }}
            justify="flex-start"
            align="center"
          >
            <Box width="100%" maxW="420px">
              <Tabs
                index={activeTab}
                onChange={setActiveTab}
                colorScheme="blue"
                variant="soft-rounded"
                isFitted
              >
                <TabList
                  mb={8}
                  bg={tabListBg}
                  p={1.5}
                  borderRadius="xl"
                  boxShadow="sm"
                >
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: tabHoverBg }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign In
                  </Tab>
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: tabHoverBg }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign Up
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Login Tab */}
                  <TabPanel p={0}>
                    <VStack spacing={7} align="stretch">
                      {showAccountSelector && (
                        <VStack spacing={4} align="stretch">
                          <Tooltip
                            label="This device remembers your recent accounts"
                            hasArrow
                            bg={tooltipBg}
                            color={tooltipColor}
                            placement="top"
                            fontSize="xs"
                          >
                            <Box
                              p={5}
                              borderRadius="16px"
                              border="1px solid"
                              borderColor={accountBoxBorder}
                              bg={accountBoxBg}
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{
                                borderColor: "#0B4885",
                                transform: "translateY(-2px)",
                                boxShadow: "lg",
                              }}
                              _active={{ transform: "scale(0.98)" }}
                              onClick={() =>
                                handleAccountSelect(primaryAccount)
                              }
                            >
                              <HStack spacing={4} align="center">
                                <Avatar
                                  name={primaryAccount.email}
                                  size="md"
                                  bg="#0B4885"
                                  color="white"
                                  fontWeight="bold"
                                />
                                <VStack align="flex-start" spacing={1} flex="1">
                                  <HStack spacing={2}>
                                    <Text
                                      fontSize="md"
                                      fontWeight="700"
                                      color={headingColor}
                                    >
                                      Continue as{" "}
                                      {getDisplayName(primaryAccount.email)}
                                    </Text>
                                    <Icon
                                      as={LockIcon}
                                      boxSize={3.5}
                                      color={iconColor}
                                    />
                                  </HStack>
                                  <Text fontSize="sm" color={textColor}>
                                    {showStoredEmail
                                      ? primaryAccount.email
                                      : primaryAccount.maskedEmail}
                                  </Text>
                                  <Text
                                    fontSize="xs"
                                    color={secondaryTextColor}
                                  >
                                    Fast re-entry with password
                                  </Text>
                                </VStack>
                                <HStack spacing={1}>
                                  <IconButton
                                    icon={
                                      showStoredEmail ? (
                                        <ViewOffIcon />
                                      ) : (
                                        <ViewIcon />
                                      )
                                    }
                                    size="sm"
                                    variant="ghost"
                                    aria-label={
                                      showStoredEmail
                                        ? "Hide saved email"
                                        : "Show saved email"
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowStoredEmail((current) => !current);
                                    }}
                                  />
                                  <IconButton
                                    icon={<ViewOffIcon />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Remove saved account"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearStoredAccount(
                                        primaryAccount.email,
                                      );
                                    }}
                                    _hover={{ bg: "red.50", color: "red.600" }}
                                  />
                                </HStack>
                              </HStack>
                            </Box>
                          </Tooltip>

                          {secondaryAccounts.length > 0 && (
                            <VStack spacing={3} align="stretch">
                              {secondaryAccounts.map((account) => (
                                <Box
                                  key={account.email}
                                  p={4}
                                  borderRadius="16px"
                                  border="1px solid"
                                  borderColor={secondaryAccountBorder}
                                  bg={secondaryAccountBg}
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  _hover={{
                                    borderColor: "#0B4885",
                                    boxShadow: "md",
                                    transform: "translateY(-1px)",
                                  }}
                                  _active={{ transform: "scale(0.98)" }}
                                  onClick={() => handleAccountSelect(account)}
                                >
                                  <HStack spacing={4}>
                                    <Avatar
                                      name={account.email}
                                      size="sm"
                                      bg="gray.700"
                                      color="white"
                                      fontWeight="bold"
                                    />
                                    <VStack
                                      align="flex-start"
                                      spacing={0}
                                      flex="1"
                                    >
                                      <Text
                                        fontSize="sm"
                                        fontWeight="600"
                                        color={textColorDark}
                                      >
                                        {getDisplayName(account.email)}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color={secondaryTextColor}
                                      >
                                        {account.maskedEmail}
                                      </Text>
                                    </VStack>
                                    <IconButton
                                      icon={<ViewOffIcon />}
                                      size="sm"
                                      variant="ghost"
                                      aria-label={`Remove saved account ${account.email}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearStoredAccount(account.email);
                                      }}
                                      _hover={{
                                        bg: "red.50",
                                        color: "red.600",
                                      }}
                                    />
                                  </HStack>
                                </Box>
                              ))}
                            </VStack>
                          )}

                          <Link
                            fontSize="sm"
                            color={textColor}
                            fontWeight="500"
                            textAlign="center"
                            onClick={handleUseDifferentAccount}
                            _hover={{
                              color: "#0B4885",
                              textDecoration: "underline",
                            }}
                            cursor="pointer"
                          >
                            Use another account
                          </Link>
                        </VStack>
                      )}

                      {/* Login Title */}
                      <VStack spacing={2} align="flex-start">
                        <Heading
                          fontSize="3xl"
                          color={headingColor}
                          fontWeight="700"
                          letterSpacing="-0.5px"
                        >
                          Welcome Back
                        </Heading>
                        <Text fontSize="md" color={textColor} fontWeight="400">
                          {selectedAccount
                            ? `Signing in as ${getDisplayName(selectedAccount.email)}`
                            : showAccountSelector
                              ? "Select a saved account to continue faster"
                              : "Sign in to continue to NoraHuman"}
                        </Text>
                      </VStack>

                      {/* Login Form Fields */}
                      <VStack spacing={5}>
                        <Collapse
                          in={!showAccountSelector}
                          animateOpacity
                          style={{ width: "100%" }}
                        >
                          <VStack spacing={5} align="stretch">
                            <FormControl>
                              <FormLabel
                                fontSize="sm"
                                fontWeight="600"
                                color={labelColor}
                                mb={2}
                              >
                                Email Address
                              </FormLabel>
                              <Input
                                name="email"
                                type="email"
                                value={loginForm.email}
                                onChange={handleLoginChange}
                                onKeyPress={handleLoginKeyPress}
                                placeholder="Enter your email"
                                size="lg"
                                bg={selectedAccount ? inputDisabledBg : inputBg}
                                border="1px solid"
                                borderColor={inputBorderColor}
                                _hover={{
                                  borderColor: inputBorderHover,
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                }}
                                _focus={{
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                  borderColor: "#0B4885",
                                  boxShadow: "0 0 0 1px #0B4885",
                                }}
                                borderRadius="lg"
                                isReadOnly={!!selectedAccount}
                                cursor={
                                  selectedAccount ? "not-allowed" : "text"
                                }
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel
                                fontSize="sm"
                                fontWeight="600"
                                color={labelColor}
                                mb={2}
                              >
                                Password
                              </FormLabel>
                              <InputGroup>
                                <Input
                                  type={showLoginPassword ? "text" : "password"}
                                  name="password"
                                  value={loginForm.password}
                                  ref={passwordInputRef}
                                  onChange={handleLoginChange}
                                  onKeyPress={handleLoginKeyPress}
                                  placeholder={
                                    selectedAccount
                                      ? "Enter your password to continue"
                                      : "Enter your password"
                                  }
                                  size="lg"
                                  bg={inputBg}
                                  border="1px solid"
                                  borderColor={inputBorderColor}
                                  _hover={{
                                    borderColor: inputBorderHover,
                                    bg: inputBgHover,
                                  }}
                                  _focus={{
                                    bg: inputBgHover,
                                    borderColor: "#0B4885",
                                    boxShadow: "0 0 0 1px #0B4885",
                                  }}
                                  borderRadius="lg"
                                />
                                <InputRightElement width="3rem">
                                  <IconButton
                                    h="1.75rem"
                                    size="sm"
                                    onClick={() =>
                                      setShowLoginPassword(!showLoginPassword)
                                    }
                                    icon={
                                      showLoginPassword ? (
                                        <ViewOffIcon />
                                      ) : (
                                        <ViewIcon />
                                      )
                                    }
                                    aria-label={
                                      showLoginPassword
                                        ? "Hide password"
                                        : "Show password"
                                    }
                                    variant="ghost"
                                  />
                                </InputRightElement>
                              </InputGroup>
                            </FormControl>

                            <HStack justify="flex-end" w="full">
                              <Link
                                fontSize="sm"
                                color="#0B4885"
                                fontWeight="600"
                                _hover={{ textDecoration: "underline" }}
                                cursor="pointer"
                                onClick={() => {
                                  setForgotPasswordEmail(
                                    selectedDisplayAccount?.email ||
                                      loginForm.email,
                                  );
                                  onForgotPasswordOpen();
                                }}
                              >
                                Forgot password?
                              </Link>
                            </HStack>

                            {selectedAccount && (
                              <Link
                                fontSize="sm"
                                color={textColor}
                                fontWeight="500"
                                alignSelf="center"
                                onClick={handleUseDifferentAccount}
                                _hover={{
                                  color: "#0B4885",
                                  textDecoration: "underline",
                                }}
                                cursor="pointer"
                              >
                                Use another account
                              </Link>
                            )}

                            <Button
                              bg="#0B4885"
                              color="white"
                              size="lg"
                              width="100%"
                              onClick={handleLogin}
                              isLoading={isLoading}
                              fontSize="md"
                              fontWeight="600"
                              _hover={{
                                bg: "#0a3d6e",
                                transform: "translateY(-1px)",
                                boxShadow: "lg",
                              }}
                              _active={{
                                bg: "#083156",
                                transform: "translateY(0)",
                              }}
                              mt={2}
                              borderRadius="lg"
                              transition="all 0.2s"
                              boxShadow="md"
                            >
                              {selectedAccount ? "Continue" : "Sign In"}
                            </Button>
                          </VStack>
                        </Collapse>

                        {/* Divider */}
                        <HStack spacing={4} my={1}>
                          <Divider borderColor={dividerColor} />
                          <Text
                            fontSize="sm"
                            color={secondaryTextColor}
                            fontWeight="500"
                            whiteSpace="nowrap"
                          >
                            OR
                          </Text>
                          <Divider borderColor={dividerColor} />
                        </HStack>

                        {/* Outlook Sign In */}
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          leftIcon={<Icon as={FaWindows} color="#0078D4" />}
                          fontSize="md"
                          fontWeight="600"
                          borderColor={outlineButtonBorder}
                          color={outlineButtonColor}
                          onClick={() => handleMicrosoftAuth("login")}
                          isLoading={isMicrosoftLoading}
                          loadingText="Connecting..."
                          _hover={{
                            bg: outlineButtonHoverBg,
                            borderColor: inputBorderHover,
                            transform: "translateY(-1px)",
                            boxShadow: "md",
                          }}
                          _active={{
                            bg: outlineButtonActiveBg,
                            transform: "translateY(0)",
                          }}
                          borderRadius="lg"
                          transition="all 0.2s"
                        >
                          Sign in with Outlook
                        </Button>
                      </VStack>
                    </VStack>
                  </TabPanel>

                  {/* Register Tab */}
                  <TabPanel p={0}>
                    <VStack spacing={6} align="stretch">
                      {/* Register Title */}
                      <VStack spacing={2} align="flex-start" mb={2}>
                        <Heading
                          fontSize="3xl"
                          fontWeight="700"
                          letterSpacing="-0.5px"
                          color={headingColor}
                        >
                          Create Your Account
                        </Heading>
                        <Text fontSize="md" color={textColor}>
                          Start your HR management journey today
                        </Text>
                      </VStack>

                      {/* Register Form Fields */}
                      <VStack spacing={5}>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          leftIcon={<Icon as={FaWindows} color="#0078D4" />}
                          fontSize="md"
                          fontWeight="600"
                          borderColor={outlineButtonBorder}
                          color={outlineButtonColor}
                          onClick={() => handleMicrosoftAuth("register")}
                          isLoading={isMicrosoftLoading}
                          loadingText="Connecting..."
                          _hover={{
                            bg: outlineButtonHoverBg,
                            borderColor: inputBorderHover,
                            transform: "translateY(-1px)",
                            boxShadow: "md",
                          }}
                          _active={{
                            bg: outlineButtonActiveBg,
                            transform: "translateY(0)",
                          }}
                          borderRadius="lg"
                          transition="all 0.2s"
                        >
                          Sign up with Outlook
                        </Button>

                        <HStack spacing={4} my={1} width="100%">
                          <Divider borderColor={dividerColor} />
                          <Text
                            fontSize="sm"
                            color={secondaryTextColor}
                            fontWeight="500"
                            whiteSpace="nowrap"
                          >
                            OR
                          </Text>
                          <Divider borderColor={dividerColor} />
                        </HStack>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Full Name
                          </FormLabel>
                          <Input
                            name="name"
                            value={registerForm.name}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your full name"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{
                              borderColor: inputBorderHover,
                              bg: inputBgHover,
                            }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Email Address
                          </FormLabel>
                          <Input
                            name="email"
                            type="email"
                            value={registerForm.email}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your email"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{
                              borderColor: inputBorderHover,
                              bg: inputBgHover,
                            }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Phone Number
                          </FormLabel>
                          <Input
                            name="phone"
                            type="tel"
                            value={registerForm.phone}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your phone number"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{
                              borderColor: inputBorderHover,
                              bg: inputBgHover,
                            }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Company Name
                          </FormLabel>
                          <Input
                            name="companyName"
                            value={registerForm.companyName}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your company name"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{
                              borderColor: inputBorderHover,
                              bg: inputBgHover,
                            }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showRegisterPassword ? "text" : "password"}
                              name="password"
                              value={registerForm.password}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Create a password"
                              size="lg"
                              bg={inputBg}
                              border="1px solid"
                              borderColor={inputBorderColor}
                              _hover={{
                                borderColor: inputBorderHover,
                                bg: inputBgHover,
                              }}
                              _focus={{
                                bg: inputBgHover,
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowRegisterPassword(!showRegisterPassword)
                                }
                                icon={
                                  showRegisterPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showRegisterPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color={labelColor}
                            mb={2}
                          >
                            Confirm Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={registerForm.confirmPassword}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Confirm your password"
                              size="lg"
                              bg={inputBg}
                              border="1px solid"
                              borderColor={inputBorderColor}
                              _hover={{
                                borderColor: inputBorderHover,
                                bg: inputBgHover,
                              }}
                              _focus={{
                                bg: inputBgHover,
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                icon={
                                  showConfirmPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showConfirmPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <Button
                          bg="#0B4885"
                          color="white"
                          size="lg"
                          width="100%"
                          onClick={handleRegister}
                          isLoading={isLoading}
                          fontSize="md"
                          fontWeight="600"
                          _hover={{
                            bg: "#0a3d6e",
                            transform: "translateY(-1px)",
                            boxShadow: "lg",
                          }}
                          _active={{
                            bg: "#083156",
                            transform: "translateY(0)",
                          }}
                          mt={2}
                          borderRadius="lg"
                          transition="all 0.2s"
                          boxShadow="md"
                        >
                          Create Account
                        </Button>

                        <Text
                          fontSize="xs"
                          color={secondaryTextColor}
                          textAlign="center"
                          lineHeight="1.5"
                        >
                          By signing up, you agree to our{" "}
                          <Link
                            color="#0B4885"
                            fontWeight="600"
                            _hover={{ textDecoration: "underline" }}
                          >
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link
                            color="#0B4885"
                            fontWeight="600"
                            _hover={{ textDecoration: "underline" }}
                          >
                            Privacy Policy
                          </Link>
                        </Text>
                      </VStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {/* Company Selection Modal */}
      <Modal
        isOpen={showCompanySelector}
        onClose={() => {
          setShowCompanySelector(false);
          setAvailableCompanies([]);
        }}
        isCentered
        size="lg"
      >
        <ModalOverlay bg="rgba(9, 17, 12, 0.46)" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="2xl"
          mx={4}
          bg={modalBg}
          border="1px solid"
          borderColor={useColorModeValue(
            "rgba(11,72,133,0.10)",
            "rgba(255,255,255,0.08)",
          )}
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
          overflow="hidden"
        >
          <Box
            h="4px"
            bg="linear-gradient(90deg, #163d2a 0%, #28543d 50%, #8ea97b 100%)"
          />
          <ModalHeader>
            <VStack align="flex-start" spacing={3}>
              <Text
                fontSize="10px"
                fontWeight="700"
                letterSpacing="0.28em"
                textTransform="uppercase"
                color={secondaryTextColor}
              >
                Company Access
              </Text>
              <Heading
                fontSize={{ base: "xl", md: "2xl" }}
                color={headingColor}
              >
                Select Your Company
              </Heading>
              <Text
                fontSize="sm"
                fontWeight="normal"
                color={textColor}
                maxW="440px"
                lineHeight="1.7"
              >
                Your account is associated with multiple companies. Please
                select one to continue.
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <VStack spacing={4} align="stretch">
              {availableCompanies.map((company) => (
                <Box
                  key={company.companySlug}
                  p={5}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={useColorModeValue(
                    "rgba(22,61,42,0.12)",
                    "rgba(255,255,255,0.08)",
                  )}
                  bg={useColorModeValue(
                    "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,248,0.96) 100%)",
                    "linear-gradient(180deg, rgba(26,32,44,0.96) 0%, rgba(17,24,39,0.98) 100%)",
                  )}
                  cursor="pointer"
                  position="relative"
                  transition="all 0.24s ease"
                  _hover={{
                    borderColor: "rgba(22,61,42,0.40)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 16px 40px rgba(22, 61, 42, 0.12)",
                  }}
                  onClick={() => handleCompanySelect(company.companySlug)}
                >
                  <HStack
                    align="flex-start"
                    justify="space-between"
                    spacing={4}
                  >
                    <VStack align="flex-start" spacing={2} flex="1">
                      <Text
                        fontSize="lg"
                        fontWeight="700"
                        color={headingColor}
                        letterSpacing="-0.02em"
                      >
                        {company.companyName}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        <Box
                          px={2.5}
                          py={1}
                          borderRadius="full"
                          bg={useColorModeValue(
                            "rgba(22,61,42,0.08)",
                            "rgba(255,255,255,0.08)",
                          )}
                        >
                          <Text
                            fontSize="10px"
                            fontWeight="700"
                            letterSpacing="0.14em"
                            textTransform="uppercase"
                            color={textColor}
                          >
                            {company.companySlug}
                          </Text>
                        </Box>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          {company.role}
                        </Text>
                      </HStack>
                    </VStack>

                    <Flex
                      w="38px"
                      h="38px"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      border="1px solid"
                      borderColor={useColorModeValue(
                        "rgba(22,61,42,0.18)",
                        "rgba(255,255,255,0.12)",
                      )}
                      bg={useColorModeValue(
                        "rgba(255,255,255,0.7)",
                        "rgba(255,255,255,0.04)",
                      )}
                      color={headingColor}
                      fontSize="lg"
                      fontWeight="500"
                      flexShrink={0}
                    >
                      ›
                    </Flex>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter pt={1}>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCompanySelector(false);
                setAvailableCompanies([]);
              }}
              fontWeight="600"
              color={textColor}
              _hover={{ bg: outlineButtonHoverBg }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={isForgotPasswordOpen}
        onClose={onForgotPasswordClose}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4} bg={modalBg}>
          <ModalHeader>
            <VStack align="flex-start" spacing={1}>
              <Heading fontSize="2xl" color={headingColor}>
                Reset Password
              </Heading>
              <Text fontSize="sm" fontWeight="normal" color={textColor}>
                Enter your email address and we'll send you a link to reset your
                password
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                Email Address
              </FormLabel>
              <Input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="Enter your email"
                size="lg"
                bg={inputBg}
                color={textColorDark}
                border="1px solid"
                borderColor={inputBorderColor}
                _placeholder={{ color: secondaryTextColor }}
                _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                _focus={{
                  bg: inputBgHover,
                  borderColor: "#0B4885",
                  boxShadow: "0 0 0 1px #0B4885",
                }}
                borderRadius="lg"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleForgotPassword();
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={onForgotPasswordClose}
              size="lg"
              fontWeight="600"
              _hover={{ bg: outlineButtonActiveBg }}
            >
              Cancel
            </Button>
            <Button
              bg="#0B4885"
              color="white"
              size="lg"
              onClick={handleForgotPassword}
              isLoading={isSendingReset}
              loadingText="Sending..."
              fontWeight="600"
              _hover={{
                bg: "#0a3d6e",
                transform: "translateY(-1px)",
                boxShadow: "lg",
              }}
              _active={{
                bg: "#083156",
                transform: "translateY(0)",
              }}
              borderRadius="lg"
              transition="all 0.2s"
            >
              Send Reset Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default LoginPage;
